import type {
  LocalizedNotificationKind,
  NotificationMessage,
  NotificationState,
  WorldNotification,
} from "../../models/notifications.ts";

export type {
  LocalizedNotificationKind,
  NotificationMessage,
  NotificationState,
  WorldNotification,
};

export function activeCount(state: NotificationState): number {
  return state.items.length;
}

export function currentNotification(state: NotificationState): WorldNotification | null {
  if (state.items.length === 0) {
    return null;
  }
  const index = Math.min(state.selectedIndex, state.items.length - 1);
  return state.items[index] ?? null;
}

function clampSelectedIndex(items: WorldNotification[], selectedIndex: number): number {
  if (items.length === 0) {
    return 0;
  }
  return Math.min(selectedIndex, items.length - 1);
}

export interface PushNotificationInput {
  title?: string;
  body?: string;
  messages?: NotificationMessage[];
  localizedKind?: LocalizedNotificationKind;
  localizedKinds?: LocalizedNotificationKind[];
  requiresDecision?: boolean;
}

function normalizeRawMessages(input: PushNotificationInput): NotificationMessage[] | null {
  if (input.messages && input.messages.length > 0) {
    return input.messages;
  }
  if (typeof input.title === "string" && typeof input.body === "string") {
    return [{ title: input.title, body: input.body }];
  }
  return null;
}

export function isValidPushNotificationInput(input: PushNotificationInput): boolean {
  const hasLocalizedKinds = (input.localizedKinds?.length ?? 0) > 0;
  const hasLocalizedKind = input.localizedKind !== undefined;
  const rawMessages = normalizeRawMessages(input);
  const hasRaw = rawMessages !== null;
  const sourceCount = [hasLocalizedKinds, hasLocalizedKind, hasRaw].filter(Boolean).length;
  if (sourceCount !== 1) {
    return false;
  }

  const pageCount = hasLocalizedKinds
    ? input.localizedKinds!.length
    : hasRaw
      ? rawMessages!.length
      : 1;
  if (input.requiresDecision && pageCount > 1) {
    return false;
  }

  return true;
}

export function pushNotification(
  state: NotificationState,
  input: PushNotificationInput,
): { state: NotificationState; id: string } {
  if (!isValidPushNotificationInput(input)) {
    throw new Error("Invalid PushNotificationInput");
  }
  const id = `notification-${state.nextId}`;
  const messages = normalizeRawMessages(input) ?? undefined;
  const notification: WorldNotification = {
    id,
    localizedKind: input.localizedKind,
    localizedKinds: input.localizedKinds,
    messages,
    messageIndex: 0,
    requiresDecision: input.requiresDecision ?? false,
  };
  const items = [...state.items, notification];
  return {
    state: {
      ...state,
      items,
      selectedIndex: items.length - 1,
      nextId: state.nextId + 1,
    },
    id,
  };
}

export function dismissNotification(state: NotificationState, id: string): NotificationState {
  const index = state.items.findIndex((item) => item.id === id);
  if (index === -1) {
    return state;
  }
  const items = state.items.filter((item) => item.id !== id);
  let selectedIndex = state.selectedIndex;
  if (index < selectedIndex) {
    selectedIndex -= 1;
  } else if (index === selectedIndex && selectedIndex >= items.length) {
    selectedIndex = Math.max(0, items.length - 1);
  }
  return {
    ...state,
    items,
    selectedIndex: clampSelectedIndex(items, selectedIndex),
  };
}

function notificationPageCount(notification: WorldNotification): number {
  if (notification.localizedKinds?.length) {
    return notification.localizedKinds.length;
  }
  if (notification.localizedKind) {
    return 1;
  }
  return notification.messages?.length ?? 0;
}

function isMultiPage(notification: WorldNotification): boolean {
  return notificationPageCount(notification) > 1;
}

function updateMessageIndex(
  state: NotificationState,
  notificationId: string,
  messageIndex: number,
): NotificationState {
  const items = state.items.map((item) =>
    item.id === notificationId ? { ...item, messageIndex } : item,
  );
  return { ...state, items };
}

export function canSelectPrevious(state: NotificationState): boolean {
  const notification = currentNotification(state);
  if (!notification) {
    return false;
  }
  if (isMultiPage(notification)) {
    return notification.messageIndex > 0 || state.items.length > 1;
  }
  return state.items.length > 1;
}

export function canSelectNext(state: NotificationState): boolean {
  const notification = currentNotification(state);
  if (!notification) {
    return false;
  }
  if (isMultiPage(notification)) {
    const onLastPage = notification.messageIndex >= notificationPageCount(notification) - 1;
    return !onLastPage || state.items.length > 1;
  }
  return state.items.length > 1;
}

export function shouldShowNavRow(state: NotificationState): boolean {
  const notification = currentNotification(state);
  if (!notification) {
    return false;
  }
  return isMultiPage(notification) || state.items.length > 1;
}

export function resolveNotificationNavIndicator(
  state: NotificationState,
): { current: number; total: number; isPage: boolean } | null {
  if (!shouldShowNavRow(state)) {
    return null;
  }

  const notification = currentNotification(state);
  if (!notification) {
    return null;
  }

  if (isMultiPage(notification)) {
    return {
      current: notification.messageIndex + 1,
      total: notificationPageCount(notification),
      isPage: true,
    };
  }

  return { current: state.selectedIndex + 1, total: state.items.length, isPage: false };
}

export function selectPrevious(state: NotificationState): NotificationState {
  const notification = currentNotification(state);
  if (!notification) {
    return state;
  }
  if (isMultiPage(notification) && notification.messageIndex > 0) {
    return updateMessageIndex(state, notification.id, notification.messageIndex - 1);
  }
  if (state.items.length <= 1) {
    return state;
  }
  const selectedIndex =
    state.selectedIndex === 0 ? state.items.length - 1 : state.selectedIndex - 1;
  return { ...state, selectedIndex };
}

export function selectNext(state: NotificationState): NotificationState {
  const notification = currentNotification(state);
  if (!notification) {
    return state;
  }
  if (isMultiPage(notification)) {
    const lastIndex = notificationPageCount(notification) - 1;
    if (notification.messageIndex < lastIndex) {
      return updateMessageIndex(state, notification.id, notification.messageIndex + 1);
    }
  }
  if (state.items.length <= 1) {
    return state;
  }
  const selectedIndex =
    state.selectedIndex === state.items.length - 1 ? 0 : state.selectedIndex + 1;
  return { ...state, selectedIndex };
}

export function resolveDecision(
  state: NotificationState,
  id: string,
): NotificationState {
  const notification = state.items.find((item) => item.id === id);
  if (!notification?.requiresDecision) {
    return state;
  }
  return dismissNotification(state, id);
}

export type LocalizedKindResolver = (
  kind: LocalizedNotificationKind,
) => NotificationMessage;

export function currentMessage(
  state: NotificationState,
  resolveKind: LocalizedKindResolver,
): NotificationMessage | null {
  const notification = currentNotification(state);
  if (!notification) {
    return null;
  }
  const pageCount = notificationPageCount(notification);
  const index = Math.min(notification.messageIndex, pageCount - 1);

  if (notification.localizedKinds?.length) {
    const kind = notification.localizedKinds[index];
    return kind ? resolveKind(kind) : null;
  }
  if (notification.localizedKind) {
    return resolveKind(notification.localizedKind);
  }
  return notification.messages?.[index] ?? null;
}

export function formatNotificationPageOf(
  template: string,
  messageIndex: number,
  totalPages: number,
): string {
  return template
    .replace("{current}", String(messageIndex + 1))
    .replace("{total}", String(totalPages));
}

export interface NotificationNavIndicatorTemplates {
  queue: string;
  page: string;
}

export function formatNotificationNavIndicatorText(
  indicator: { current: number; total: number; isPage: boolean },
  templates: NotificationNavIndicatorTemplates,
): string {
  return formatNotificationPageOf(
    indicator.isPage ? templates.page : templates.queue,
    indicator.current - 1,
    indicator.total,
  );
}

export function resolveNotificationNavIndicatorText(
  state: NotificationState,
  templates: NotificationNavIndicatorTemplates,
): string | null {
  const indicator = resolveNotificationNavIndicator(state);
  if (!indicator) {
    return null;
  }
  return formatNotificationNavIndicatorText(indicator, templates);
}
